import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, RotateCcw, Save, Shield, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { usePlantillasContratos, type TipoContrato } from '@/hooks/usePlantillasContratos';
import {
  plantillaPorDefecto,
  type ClausulaContrato,
} from '@/lib/contratos/plantillasPorDefecto';

function Editor({ tipo }: { tipo: TipoContrato }) {
  const { getPlantilla, upsertPlantilla, restaurarPorDefecto } = usePlantillasContratos();
  const [nombre, setNombre] = useState('');
  const [objeto, setObjeto] = useState('');
  const [clausulas, setClausulas] = useState<ClausulaContrato[]>([]);
  const [textoLegal, setTextoLegal] = useState('');
  const [saving, setSaving] = useState(false);

  const recargar = () => {
    const p = getPlantilla(tipo);
    setNombre(p.nombre);
    setObjeto(p.objeto);
    setClausulas(p.clausulas.map(c => ({ ...c })));
    setTextoLegal(p.texto_legal);
  };

  useEffect(() => { recargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tipo, getPlantilla]);

  const guardar = async () => {
    setSaving(true);
    try {
      await upsertPlantilla(tipo, { nombre, objeto, clausulas, texto_legal: textoLegal });
      toast.success('Plantilla guardada');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const restaurar = async () => {
    if (!confirm('¿Restaurar la plantilla por defecto? Se sobrescribirá la actual.')) return;
    try {
      await restaurarPorDefecto(tipo);
      const def = plantillaPorDefecto(tipo);
      setNombre(def.nombre);
      setObjeto(def.objeto);
      setClausulas(def.clausulas);
      setTextoLegal(def.texto_legal);
      toast.success('Plantilla restaurada');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const addClausula = () => setClausulas(prev => [...prev, { titulo: '', contenido: '' }]);
  const removeClausula = (i: number) => setClausulas(prev => prev.filter((_, idx) => idx !== i));
  const update = (i: number, patch: Partial<ClausulaContrato>) =>
    setClausulas(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  return (
    <div className="space-y-4">
      <div>
        <Label>Nombre de la plantilla</Label>
        <Input value={nombre} onChange={e => setNombre(e.target.value)} />
      </div>

      <div>
        <Label>Objeto del contrato</Label>
        <Textarea value={objeto} onChange={e => setObjeto(e.target.value)} rows={3} />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Cláusulas (texto plano — sin HTML)</Label>
          <Button type="button" size="sm" variant="outline" onClick={addClausula}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Añadir cláusula
          </Button>
        </div>
        {clausulas.map((c, i) => (
          <div key={i} className="rounded-md border p-3 space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Título"
                value={c.titulo}
                onChange={e => update(i, { titulo: e.target.value })}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeClausula(i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea
              placeholder="Contenido"
              value={c.contenido}
              onChange={e => update(i, { contenido: e.target.value })}
              rows={5}
            />
          </div>
        ))}
      </div>

      <div>
        <Label>Texto legal (pie del contrato)</Label>
        <Textarea value={textoLegal} onChange={e => setTextoLegal(e.target.value)} rows={3} />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={restaurar}>
          <RotateCcw className="h-4 w-4 mr-2" /> Restaurar por defecto
        </Button>
        <Button onClick={guardar} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Guardando…' : 'Guardar plantilla'}
        </Button>
      </div>
    </div>
  );
}

export function PlantillasContratosManager() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Plantillas de contratos de mantenimiento</CardTitle>
        <CardDescription>
          Personaliza las cláusulas base que se usarán al generar un nuevo contrato. El texto se almacena
          en plano (sin HTML) para garantizar que el PDF se renderice correctamente.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="seguridad">
          <TabsList>
            <TabsTrigger value="seguridad" className="gap-2">
              <Shield className="h-4 w-4" /> Seguridad
            </TabsTrigger>
            <TabsTrigger value="pci" className="gap-2">
              <Flame className="h-4 w-4" /> PCI
            </TabsTrigger>
          </TabsList>
          <TabsContent value="seguridad" className="pt-4">
            <Editor tipo="seguridad" />
          </TabsContent>
          <TabsContent value="pci" className="pt-4">
            <Editor tipo="pci" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
